import { useEffect, useRef, useState, memo } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Announces route changes to screen readers.
 * Uses an aria-live region that updates whenever the pathname changes.
 */

const routeTitles: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/subscribers': 'Subscribers',
  '/billing': 'Billing',
  '/plans': 'Plans',
  '/tickets': 'Tickets',
  '/noc': 'NOC',
  '/olt': 'OLT and GPON',
  '/nas': 'NAS and Routers',
  '/reports': 'Reports',
  '/settings': 'Settings',
  '/logs': 'Audit Logs',
};

function getPageTitle(pathname: string): string {
  // Exact match
  if (routeTitles[pathname]) return routeTitles[pathname];

  // Prefix match (e.g. /subscribers/123 → Subscribers)
  const prefix = Object.keys(routeTitles).find((key) => pathname.startsWith(key + '/'));
  if (prefix) return `${routeTitles[prefix]} detail`;

  return 'Page';
}

export const RouteAnnouncer = memo(function RouteAnnouncer() {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState('');
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Don't announce on first render (page load)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const title = getPageTitle(location.pathname);
    setAnnouncement(`Navigated to ${title}`);
  }, [location.pathname]);

  return (
    <div
      role="status"
      aria-live="assertive"
      aria-atomic="true"
      className="sr-only"
    >
      {announcement}
    </div>
  );
});
